import { create } from 'zustand';

import { defaultSimulatorState } from '@/domain/defaults';
import type { SimulationRecord } from '@/domain/serverTypes';
import type { Category, FixedCost, RecurringCost, SimulatorState, Tier } from '@/domain/types';
import { pb } from '@/lib/pocketbase';

type SimulatorActions = {
  updateInfo: (patch: Partial<NonNullable<SimulatorState['info']>>) => void;
  updateTeam: (patch: Partial<SimulatorState['team']>) => void;
  updateAcquisition: (patch: Partial<SimulatorState['acquisition']>) => void;
  updateProjection: (patch: Partial<SimulatorState['projection']>) => void;
  setInitialDeficit: (value: number) => void;

  addCategory: (category: Omit<Category, 'id'>) => void;
  updateCategory: (id: string, patch: Partial<Omit<Category, 'id'>>) => void;
  removeCategory: (id: string) => void;

  addFixedCost: (fixedCost: Omit<FixedCost, 'id'>) => void;
  updateFixedCost: (id: string, patch: Partial<Omit<FixedCost, 'id'>>) => void;
  removeFixedCost: (id: string) => void;

  addRecurringCost: (recurringCost: Omit<RecurringCost, 'id'>) => void;
  updateRecurringCost: (id: string, patch: Partial<Omit<RecurringCost, 'id'>>) => void;
  removeRecurringCost: (id: string) => void;

  addTier: (tier: Omit<Tier, 'id'>) => void;
  updateTier: (id: string, patch: Partial<Omit<Tier, 'id'>>) => void;
  removeTier: (id: string) => void;

  resetState: () => void;
  importState: (state: SimulatorState) => void;

  loadSimulation: (id: string) => Promise<void>;
};

type ServerMeta = {
  simulationId: string | null;
  isLoading: boolean;
  isSaving: boolean;
  lastError: string | null;
};

export type SimulatorStore = SimulatorState & ServerMeta & SimulatorActions;

const withId = <T extends { id: string }>(item: Omit<T, 'id'>): T =>
  ({ ...item, id: crypto.randomUUID() }) as T;

const updateById = <T extends { id: string }>(items: T[], id: string, patch: Partial<T>): T[] =>
  items.map((item) => (item.id === id ? { ...item, ...patch } : item));

const removeById = <T extends { id: string }>(items: T[], id: string): T[] =>
  items.filter((item) => item.id !== id);

const initialMeta: ServerMeta = {
  simulationId: null,
  isLoading: false,
  isSaving: false,
  lastError: null,
};

export const useSimulatorState = create<SimulatorStore>()((set) => ({
  ...defaultSimulatorState,
  ...initialMeta,

  updateInfo: (patch) => set((state) => ({ info: { ...state.info, ...patch } })),
  updateTeam: (patch) => set((state) => ({ team: { ...state.team, ...patch } })),
  updateAcquisition: (patch) =>
    set((state) => ({ acquisition: { ...state.acquisition, ...patch } })),
  updateProjection: (patch) => set((state) => ({ projection: { ...state.projection, ...patch } })),
  setInitialDeficit: (value) => set({ initialDeficit: value }),

  addCategory: (category) =>
    set((state) => ({ categories: [...state.categories, withId<Category>(category)] })),
  updateCategory: (id, patch) =>
    set((state) => ({ categories: updateById<Category>(state.categories, id, patch) })),
  removeCategory: (id) => set((state) => ({ categories: removeById(state.categories, id) })),

  addFixedCost: (fixedCost) =>
    set((state) => ({ fixedCosts: [...state.fixedCosts, withId<FixedCost>(fixedCost)] })),
  updateFixedCost: (id, patch) =>
    set((state) => ({ fixedCosts: updateById<FixedCost>(state.fixedCosts, id, patch) })),
  removeFixedCost: (id) => set((state) => ({ fixedCosts: removeById(state.fixedCosts, id) })),

  addRecurringCost: (recurringCost) =>
    set((state) => ({
      recurringCosts: [...state.recurringCosts, withId<RecurringCost>(recurringCost)],
    })),
  updateRecurringCost: (id, patch) =>
    set((state) => ({
      recurringCosts: updateById<RecurringCost>(state.recurringCosts, id, patch),
    })),
  removeRecurringCost: (id) =>
    set((state) => ({ recurringCosts: removeById(state.recurringCosts, id) })),

  addTier: (tier) => set((state) => ({ tiers: [...state.tiers, withId<Tier>(tier)] })),
  updateTier: (id, patch) => set((state) => ({ tiers: updateById<Tier>(state.tiers, id, patch) })),
  removeTier: (id) => set((state) => ({ tiers: removeById(state.tiers, id) })),

  resetState: () => set({ ...defaultSimulatorState }),
  importState: (state) => set({ ...state }),

  loadSimulation: async (id) => {
    set({ isLoading: true, lastError: null });
    try {
      const record = await pb.collection('simulations').getOne<SimulationRecord>(id);
      set({ ...record.state, simulationId: record.id, isLoading: false });
    } catch {
      set({ isLoading: false, lastError: 'Impossible de charger la simulation.' });
    }
  },
}));
